import { ActivatedRouteSnapshot, NavigationEnd, NavigationError, Router } from '@angular/router';
import { Component, HostListener, OnInit, Renderer2, RendererFactory2 } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';

import { AccountService } from '../../core/auth/account.service';
import { LoaderService } from '../../shared/services/loader.service';
import { LocalStorageService } from 'ngx-webstorage';
import { Title } from '@angular/platform-browser';
import { filter } from 'rxjs';

@Component({
  selector: 'app-main',
  standalone: false,
  templateUrl: './main.component.html',
})
export class MainComponent implements OnInit {
  private renderer: Renderer2;
  isLoginOrRegisterOrReset = false;
  hideNavbar = false;
  hideTopbar = false;
  isCollapsed: boolean | undefined;
  isChat = false;
  isSettings = false;
  currentRoute = '';
  timer: any = null;

  showLoader = false;

  get isCardExtensionRoute(): boolean {
    return this.currentRoute.includes('/card-extension');
  }

  constructor(
    private localStorageService: LocalStorageService,
    private accountService: AccountService,
    private titleService: Title,
    private router: Router,
    private translateService: TranslateService,
    private loaderService: LoaderService,
    rootRenderer: RendererFactory2
  ) {
     this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        // sakrij navbar ako je ruta /payment/stripe ili payspot
        this.hideNavbar = event.urlAfterRedirects.startsWith('/payment/stripe') || event.urlAfterRedirects.startsWith('/payment/payspot') || event.urlAfterRedirects.startsWith('/payspot/uslovi-koriscenja') || event.urlAfterRedirects.startsWith('/terms-of-use') || event.urlAfterRedirects.startsWith('/privacy-policy')
        // sakrij i topbar za javnu samostalnu stranicu uslova koriscenja
        this.hideTopbar = event.urlAfterRedirects.startsWith('/payspot/uslovi-koriscenja') || event.urlAfterRedirects.startsWith('/terms-of-use') || event.urlAfterRedirects.startsWith('/privacy-policy')
      });
    this.renderer = rootRenderer.createRenderer(document.querySelector('html'), null);
  }

  ngOnInit(): void {
    // try to log in automatically
    this.accountService.identity().subscribe();
    this.isLoading();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = event.url;
        this.updateTitle();
      }
      if (event instanceof NavigationError && event.error.status === 404) {
        this.router.navigate(['/404']);
      }
    });

    this.translateService.onLangChange.subscribe((langChangeEvent: LangChangeEvent) => {
      this.updateTitle();

      this.renderer.setAttribute(document.querySelector('html'), 'lang', langChangeEvent.lang);
    });

    // Ensure localStorage is ready before setting language
    // Try multiple times with increasing delays to ensure localStorage is ready
    this.retryLanguageInitialization(0);
  }

  private retryLanguageInitialization(attempt: number): void {
    const maxAttempts = 5;
    const delay = Math.min(100 * Math.pow(2, attempt), 1000); // Exponential backoff, max 1 second

    setTimeout(() => {
      try {
        // Test if localStorage is working
        const testKey = 'test-' + Date.now();
        this.localStorageService.store(testKey, 'test');
        const testValue = this.localStorageService.retrieve(testKey);

        if (testValue === 'test') {
          // localStorage is working, proceed with language initialization
          this.localStorageService.clear(testKey); // Clean up test key
          this.initializeLanguage();
        } else {
          // localStorage not ready yet, retry
          if (attempt < maxAttempts) {
            console.log(
              `localStorage not ready, retrying in ${delay}ms (attempt ${attempt + 1}/${maxAttempts})`
            );
            this.retryLanguageInitialization(attempt + 1);
          } else {
            console.error(
              'localStorage failed to initialize after multiple attempts, using fallback'
            );
            this.translateService.use('en');
          }
        }
      } catch (error) {
        if (attempt < maxAttempts) {
          console.log(
            `localStorage error, retrying in ${delay}ms (attempt ${attempt + 1}/${maxAttempts}):`,
            error
          );
          this.retryLanguageInitialization(attempt + 1);
        } else {
          console.error('localStorage failed after multiple attempts, using fallback');
          this.translateService.use('en');
        }
      }
    }, delay);
  }

  private initializeLanguage(): void {
    try {
      // Check if localStorage service is working
      console.log('localStorage service available:', !!this.localStorageService);

      // Try to store and retrieve a test value
      this.localStorageService.store('test', 'test-value');
      const testValue = this.localStorageService.retrieve('test');
      console.log('localStorage test - stored and retrieved:', testValue);

      const langKey = this.localStorageService.retrieve('locale');
      console.log('Retrieved language from localStorage:', langKey, 'type:', typeof langKey);

      // Set a default language if none is stored
      const languageToUse = langKey || 'sr';
      console.log('Setting language to:', languageToUse, 'from stored:', langKey);

      // Ensure we always have a valid language
      if (!languageToUse || languageToUse === 'null' || languageToUse === 'undefined') {
        console.warn('Invalid language detected, using default: en');
        this.localStorageService.store('locale', 'en');
        this.translateService.use('en');
      } else {
        this.translateService.use(languageToUse);
        // Store the language in localStorage for future use
        if (!langKey) {
          this.localStorageService.store('locale', languageToUse);
        }
      }

      // Verify the language was set
      console.log('Final language set:', this.translateService.currentLang);
    } catch (error) {
      console.error('Error initializing language:', error);
      // Fallback to English
      this.translateService.use('en');
      try {
        this.localStorageService.store('locale', 'en');
      } catch (storageError) {
        console.error('Failed to store language in localStorage:', storageError);
      }
    }
  }

  private getPageTitle(routeSnapshot: ActivatedRouteSnapshot): string {
    let title: string =
      routeSnapshot.data && routeSnapshot.data['pageTitle'] ? routeSnapshot.data['pageTitle'] : '';
    if (routeSnapshot.firstChild) {
      title = this.getPageTitle(routeSnapshot.firstChild) || title;
    }

    const currentUrl = this.router.routerState.snapshot.url || '';
    const authRoots = ['/', '/account/login', '/account/register', '/account/reset/request'];
    this.isLoginOrRegisterOrReset = authRoots.some(
      (root) =>
        currentUrl === root ||
        currentUrl.startsWith(root + '?') ||
        currentUrl.startsWith(root + '/')
    );

    this.isChat = this.router.routerState.snapshot.url === '/chat/home';
    this.isSettings = currentUrl.includes('/settings') || currentUrl.includes('/account/settings');

    return 'Benefit'; // title
  }

  private updateTitle(): void {
    const pageTitle = this.getPageTitle(this.router.routerState.snapshot.root);
    // if (!pageTitle) {
    //   pageTitle = 'global.title';
    // }
    this.translateService
      .get('global.title')
      .subscribe((title) => this.titleService.setTitle(title));
  }

  isNavbarCollapsed(collapsed: boolean): void {
    setTimeout(() => {
      this.isCollapsed = collapsed;
    });
  }

  @HostListener('document:keyup', ['$event'])
  @HostListener('document:click', ['$event'])
  @HostListener('document:wheel', ['$event'])
  @HostListener('document:scroll', ['$event'])
  @HostListener('document:load', ['$event'])
  resetTimer(): void {
    clearTimeout(this.timer);
    this.timer = setTimeout(
      () => {
        if (
          this.currentRoute !== '/ids' &&
          this.currentRoute !== '/records' &&
          this.currentRoute !== '/ticket-office' &&
          !this.currentRoute.startsWith('/ids/organization')
        ) {
          this.router.navigate(['/records']);
        }
      },
      2 * 60 * 1000
    );
  }
  isLoading(): void {
    this.loaderService.isLoading.subscribe((res) => {
      this.showLoader = res;
    });
  }
}
